import { ApolloLink } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { ActivityLogsResult } from 'types/graphql'

import { setAuthRedirecting } from 'src/lib/authState'
import { mutationEventsLink } from 'src/lib/mutationEvents'

// Prevents multiple concurrent redirects when several in-flight requests
// all fail with auth errors at the same time
let isRedirectingToLogout = false

/**
 * Clears Auth0 tokens from localStorage and redirects to Auth0 logout.
 * Guarded against multiple invocations (e.g. parallel failed requests).
 * Sets a global flag so that component-level onError handlers suppress
 * their error toasts during the redirect.
 */
function redirectToAuth0Logout() {
  if (isRedirectingToLogout) return
  isRedirectingToLogout = true
  setAuthRedirecting()

  // Clear Auth0 tokens from local storage
  const localStorageKeys = Object.keys(localStorage)
  localStorageKeys.forEach((key) => {
    if (key.startsWith('@@auth0spajs@@')) {
      localStorage.removeItem(key)
    }
  })

  // Validate Auth0 domain to prevent open-redirect if env is misconfigured.
  // Auth0 custom domains are also allowed (e.g. auth.example.com).
  const domain = process.env.AUTH0_DOMAIN
  if (!domain || !/^[a-z0-9][a-z0-9\-.]+\.[a-z]{2,}$/.test(domain)) {
    // Fallback: just navigate to the app origin and let the router redirect
    // to the login page via PrivateSet.
    window.location.href = window.location.origin
    return
  }

  const clientId = process.env.AUTH0_CLIENT_ID
  const returnTo = encodeURIComponent(window.location.origin)
  const logoutUrl = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`

  window.location.href = logoutUrl
}

// Specific patterns from the RedwoodJS auth context plugin error wrapper.
// These use regex anchors to avoid false positives from user-facing data.
// The primary detection is the UNAUTHENTICATED extension code — these are
// a fallback safety net for edge cases only.
const AUTH_ERROR_REGEXES = [
  /Exception in getAuthenticationContext:/,
  /^jwt expired$/,
  /^jwt malformed$/,
  /^invalid signature$/,
]

// Auth-related error messages from Auth0 SPA SDK when getToken() fails.
// These are thrown by @auth0/auth0-spa-js internals, so the exact strings
// are stable across SDK versions.
const NETWORK_AUTH_REGEXES = [
  /^Login required$/,
  /^Missing Refresh Token/,
  /^Consent required$/,
  /^login_required$/,
  /unknown or invalid refresh token/,
]

// Pages where the error link should NOT redirect to Auth0 logout.
// Includes truly public pages (no auth required) and pages with dedicated
// PrivateSet auth redirects that handle auth failures themselves
// (e.g., driver-application → driverLogin, voe-response → voeLogin).
const AUTH_REDIRECT_EXEMPT_PATTERNS = [
  // Public pages
  /^\/login($|\?)/,
  /^\/logout($|\?)/,
  /^\/driver-login($|\?)/,
  /^\/voe-login($|\?)/,
  /^\/forbidden($|\?)/,
  /^\/short-form\//,
  /^\/public-short-form\//,
  /^\/external-voe-request\//,
  /^\/voe-opt-out\//,
  /^\/privacy\//,
  /^\/terms\//,
  /^\/c($|\/|\?)/,
  /^\/oauth-nylas-callback($|\?)/,
  // Private pages with their own PrivateSet auth redirect
  /^\/driver-application($|\/|\?)/,
  /^\/voe-response($|\/|\?)/,
]

function isAuthRedirectExempt(): boolean {
  try {
    const path = window.location.pathname
    return AUTH_REDIRECT_EXEMPT_PATTERNS.some((re) => re.test(path))
  } catch {
    return false
  }
}

/**
 * Checks whether the Auth0 session tokens have been removed from
 * localStorage (e.g. by logging out in another tab).
 */
function isAuthSessionLost(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.startsWith('@@auth0spajs@@')) {
        return false
      }
    }
    return true
  } catch {
    return false
  }
}

// Error link to handle expired/invalid authentication tokens
const errorLink = onError(({ graphQLErrors, networkError }) => {
  // Pages with their own PrivateSet auth redirect (driver-application,
  // voe-response) and public pages should not be disrupted by the global
  // Auth0 logout redirect — their route-level redirects handle auth failures.
  const exempt = isAuthRedirectExempt()

  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      // Primary check: proper UNAUTHENTICATED code from requireAuth()
      if (error.extensions?.code === 'UNAUTHENTICATED') {
        if (!exempt) {
          console.warn(
            '[Auth] UNAUTHENTICATED code detected — redirecting to logout'
          )
          redirectToAuth0Logout()
        }
        return
      }

      // Fallback: catch auth-related error messages that don't have the
      // UNAUTHENTICATED code (e.g. from authDecoder failures that slip
      // through the safe wrapper). Uses regex for precision to avoid
      // matching user-facing strings that happen to contain similar words.
      const message = error.message || ''
      if (AUTH_ERROR_REGEXES.some((re) => re.test(message))) {
        if (!exempt) {
          console.warn(
            '[Auth] Auth error regex matched — redirecting to logout:',
            message
          )
          redirectToAuth0Logout()
        }
        return
      }
    }
  }

  if (networkError) {
    // When Auth0's getTokenSilently() fails (expired refresh token, revoked
    // session), the error propagates through Apollo's setContext link as a
    // networkError rather than a graphQLError.
    const message = networkError.message || ''
    if (
      NETWORK_AUTH_REGEXES.some((re) => re.test(message)) ||
      ('statusCode' in networkError && networkError.statusCode === 401)
    ) {
      if (!exempt) {
        console.warn(
          '[Auth] Network auth error detected — redirecting to logout:',
          message
        )
        redirectToAuth0Logout()
      }
      return
    }
    console.error(`[Network error]: ${networkError}`)
  }

  // Safety net: if we're getting errors and Auth0 tokens are gone from
  // localStorage (e.g. user logged out in another tab), redirect to logout.
  // This catches cases where the server masks the auth error to a generic 500.
  if (
    (graphQLErrors?.length || networkError) &&
    !exempt &&
    isAuthSessionLost()
  ) {
    console.warn(
      '[Auth] Auth0 tokens missing from localStorage — redirecting to logout'
    )
    redirectToAuth0Logout()
  }
})

export const graphQLClientConfig = {
  link: (redwoodApolloLinks: Array<{ link: ApolloLink }>) =>
    ApolloLink.from([
      errorLink,
      mutationEventsLink,
      ...redwoodApolloLinks.map(({ link }) => link),
    ]),
  cacheConfig: {
    typePolicies: {
      TrackingLink: {
        keyFields: ['trackingLinkId'],
      },
      Employee: {
        keyFields: ['employeeId'],
      },
      // NOTE: Do NOT normalize Document by documentId. Several queries embed
      // `documentIntelligence.predictions` with different sub-selections and no
      // stable prediction id, and that nested object has no merge policy. With a
      // shared normalized Document entity, opening a document preview from an SMS
      // bubble fires GET_DOCUMENT_INTELLIGENCE (cache-and-network), which rewrites
      // that shared slot and makes GET_ACTIVITY_LOGS read incomplete — tearing
      // down the sidebar SMS thread and closing the just-opened preview modal.
      Query: {
        fields: {
          activityLogs: {
            keyArgs: ['applicantId', 'filters'],
            merge(existing = {} as ActivityLogsResult, incoming) {
              const existingItems = existing.items || []
              const incomingItems = incoming.items || []

              // Create a set of unique IDs from existing items
              const existingIds = new Set(
                existingItems.map((item) => item.activityLogId)
              )

              // Only add incoming items that don't already exist
              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.activityLogId)
                ),
              ]

              // Sort merged items by createdAt in descending order
              mergedItems.sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
              )

              return { ...incoming, items: mergedItems }
            },
          },
          callTranscripts: {
            keyArgs: ['activityLogId'],
            merge(existing, incoming) {
              const existingItems = existing?.items || []
              const incomingItems = incoming.items || []

              const existingIds = new Set(
                existingItems.map((item) => item.callTranscriptId)
              )

              // Only add incoming items that don't already exist
              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.callTranscriptId)
                ),
              ]

              // Sort merged items by createdAt in descending order
              mergedItems.sort(
                (a, b) =>
                  new Date(a.date).getTime() - new Date(b.date).getTime()
              )

              return {
                ...incoming,
                items: mergedItems,
              }
            },
          },
          automatedRules: {
            keyArgs: false as const,
            merge(existing = { items: [] }, incoming) {
              const existingItems = existing.items || []
              const incomingItems = incoming.items || []

              const existingIds = new Set(
                existingItems.map((item) => item.automatedRuleId)
              )

              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.automatedRuleId)
                ),
              ]

              return {
                ...incoming,
                items: mergedItems,
              }
            },
          },
          getBookings: {
            keyArgs: ['bookingId', 'filters', 'isInfiniteScroll'],
            merge(existing, incoming, { variables }) {
              const isInfiniteScroll = variables?.isInfiniteScroll || false
              const isInitialLoad = variables?.pageInfo?.offset === 0

              // If infinite scroll is disabled, simply replace the items
              if (!isInfiniteScroll || isInitialLoad) {
                return {
                  ...incoming,
                  items: incoming.items || [],
                }
              }

              const existingItems = existing?.items || []
              const incomingItems = incoming.items || []

              const existingIds = new Set(
                existingItems.map((item) => item.bookingId)
              )

              // Only add incoming items that don't already exist
              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.bookingId)
                ),
              ]

              return {
                ...incoming,
                items: mergedItems,
              }
            },
          },
          documentsExpirationByCategory: {
            keyArgs: ['filters', 'isInfiniteScroll'],
            merge(existing, incoming, { variables }) {
              const isInfiniteScroll = variables?.isInfiniteScroll || false
              const isInitialLoad = variables?.pageInfo?.offset === 0

              // If infinite scroll is disabled, simply replace the items
              if (!isInfiniteScroll || isInitialLoad) {
                return {
                  ...incoming,
                  items: incoming.items || [],
                }
              }

              const existingItems = existing?.items || []
              const incomingItems = incoming.items || []

              const existingIds = new Set(
                existingItems.map((item) => item.documentId)
              )

              // Only add incoming items that don't already exist
              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.documentId)
                ),
              ]

              return {
                ...incoming,
                items: mergedItems,
              }
            },
          },
          getEmailThreads: {
            keyArgs: ['input'],
            merge(existing = { items: [] }, incoming) {
              const existingItems = existing.items || []
              const incomingItems = incoming.items || []

              const existingIds = new Set(
                existingItems.map((item) => item.emailThreadId)
              )

              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.emailThreadId)
                ),
              ]

              return {
                ...incoming,
                items: mergedItems,
              }
            },
          },
          applicants: {
            keyArgs: ['filters'],
            merge(existing, incoming) {
              const existingItems = existing?.items || []
              const incomingItems = incoming.items || []

              // Create a set of unique IDs from existing items
              const existingIds = new Set(
                existingItems.map((item) => item.applicantId)
              )

              // Only add incoming items that don't already exist
              const mergedItems = [
                ...existingItems,
                ...incomingItems.filter(
                  (item) => !existingIds.has(item.applicantId)
                ),
              ]

              return {
                ...incoming,
                items: mergedItems,
              }
            },
          },
        },
      },
    },
  },
}
